import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// material
import { Menu, MenuItem, IconButton, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField } from '@mui/material';
// component
import Iconify from '../../../components/Iconify';
import useAxiosWithAuth from '../../../api/useAxiosWithAuth';

export default function ExporterMoreMenu({ id, name, onActionComplete }) {
    const ref = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [updatedName, setUpdatedName] = useState(name);
    const [updatedNameError, setUpdatedNameError] = useState(false);
    const [updatedNameHelperText, setUpdatedNameHelperText] = useState('');
    const axiosInstance = useAxiosWithAuth();
    const navigate = useNavigate();

    const handleDelete = async () => {
        try {
            // Make the DELETE API call to remove the exporter
            const response = await axiosInstance.delete(`/trade/exporter-name/${id}`);
            
            if (response.status === 200 || response.status === 204) {
                toast.success(`Exporter "${name}" deleted successfully`);
            } else {
                toast.error('Failed to delete exporter. Please try again.');
            }
            setDeleteDialogOpen(false);
            if (onActionComplete) {
                onActionComplete();
            }
        } catch (error) {
            console.error('Error deleting exporter:', error);
            // Check if the error response has the specific error message from the API
            if (error.response && error.response.data && error.response.data.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error('Failed to delete exporter');
            }
            setDeleteDialogOpen(false);
        }
    };

    const handleUpdate = async () => {
        // Basic validation
        if (!updatedName.trim()) {
            setUpdatedNameError(true);
            setUpdatedNameHelperText('Exporter name is required');
            return;
        }

        if (updatedName.trim().length < 2) {
            setUpdatedNameError(true);
            setUpdatedNameHelperText('Exporter name must be at least 2 characters');
            return;
        }

        try {
            // Make the PUT API call to update the exporter with old_name and new_name
            const response = await axiosInstance.put('/trade/exporter-name', {
                old_name: name,  // Use the original name
                new_name: updatedName.trim(),  // Use the updated name
            });

            if (response.status >= 200 && response.status < 300) {
                toast.success(`Exporter updated successfully to "${updatedName}"`);
                setUpdateDialogOpen(false);
                if (onActionComplete) {
                    onActionComplete();
                }
            } else {
                toast.error('Failed to update exporter. Please try again.');
            }
        } catch (error) {
            console.error('Error updating exporter:', error);
            // Check if the error response has the specific error message from the API
            if (error.response && error.response.data && error.response.data.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error('Failed to update exporter');
            }
        }
    };

    const handleUpdateDialogOpen = () => {
        setUpdateDialogOpen(true);
        setUpdatedName(name);
        setUpdatedNameError(false);
        setUpdatedNameHelperText('');
        setIsOpen(false); // Close the menu when opening the dialog
    };

    const handleUpdateDialogClose = () => {
        setUpdateDialogOpen(false);
        setUpdatedName(name); // Reset to original name when closing
        setUpdatedNameError(false);
        setUpdatedNameHelperText('');
    };

    return (
        <>
            <IconButton ref={ref} onClick={() => setIsOpen(true)}>
                <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
            </IconButton>

            <Menu
                open={isOpen}
                anchorEl={ref.current}
                onClose={() => setIsOpen(false)}
                PaperProps={{
                    sx: { width: 200, maxWidth: '100%' },
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem 
                    onClick={handleUpdateDialogOpen}
                    sx={{ color: 'text.secondary' }}
                >
                    <ListItemIcon>
                        <Iconify icon="akar-icons:arrow-forward-thick" width={24} height={24} />
                    </ListItemIcon>
                    <ListItemText primary="Update" primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
                {/* <MenuItem 
                    onClick={() => {
                        setIsOpen(false);
                        setDeleteDialogOpen(true);
                    }} 
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <Iconify icon="material-symbols:delete-outline" width={24} height={24} />
                    </ListItemIcon>
                    <ListItemText primary="Delete" primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem> */}
            </Menu>
            
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the exporter "{name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>
            
            {/* Update Exporter Dialog */}
            <Dialog open={updateDialogOpen} onClose={handleUpdateDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>Update Exporter</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="update-exporter-name"
                        label="Exporter Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={updatedName}
                        onChange={(e) => {
                            setUpdatedName(e.target.value);
                            // Clear error when user starts typing
                            if (updatedNameError) {
                                setUpdatedNameError(false);
                                setUpdatedNameHelperText('');
                            }
                        }}
                        error={updatedNameError}
                        helperText={updatedNameHelperText}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUpdateDialogClose}>Cancel</Button>
                    <Button onClick={handleUpdate} variant="contained">Update</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}